import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { cn } from '@/lib/utils';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();
  const itemCount = useCartStore((state) => state.getItemCount());
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-sand-100 bg-white/80 backdrop-blur-xl">
      <nav className="container-wide">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-600">
              <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor">
                <circle cx="12" cy="6" r="3" />
                <path d="M12 9v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <span className="text-lg font-semibold text-sand-900">GolfBook</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 md:flex">
            <Link to="/tee-times" className="text-sm font-medium text-sand-600 hover:text-sand-900 transition-colors">
              Tee Times
            </Link>
            <Link to="/shop" className="text-sm font-medium text-sand-600 hover:text-sand-900 transition-colors">
              Pro Shop
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/bookings" className="text-sm font-medium text-sand-600 hover:text-sand-900 transition-colors">
                  My Bookings
                </Link>
                <Link to="/loyalty" className="text-sm font-medium text-sand-600 hover:text-sand-900 transition-colors">
                  Rewards
                </Link>
              </>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <Link to="/cart" className="relative p-2 text-sand-600 hover:text-sand-900 transition-colors">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
              </svg>
              {itemCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-sage-600 text-2xs font-semibold text-white">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* Auth */}
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center gap-2 rounded-full bg-sand-100 py-2 pl-2 pr-4 text-sm font-medium text-sand-900 hover:bg-sand-200 transition-colors">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-sage-600 text-2xs font-semibold text-white">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </div>
                  <span className="hidden sm:block">{user?.firstName}</span>
                </button>
                <div className="absolute right-0 top-full mt-2 w-48 rounded-xl bg-white py-2 shadow-soft-lg border border-sand-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <Link to="/account" className="block px-4 py-2 text-sm text-sand-700 hover:bg-sand-50">
                    Account Settings
                  </Link>
                  <Link to="/bookings" className="block px-4 py-2 text-sm text-sand-700 hover:bg-sand-50">
                    My Bookings
                  </Link>
                  <Link to="/loyalty" className="block px-4 py-2 text-sm text-sand-700 hover:bg-sand-50">
                    Rewards
                  </Link>
                  <hr className="my-2 border-sand-100" />
                  <button
                    onClick={handleLogout}
                    className="block w-full px-4 py-2 text-left text-sm text-sand-700 hover:bg-sand-50"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="btn-ghost text-sm">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary text-sm hidden sm:flex">
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-sand-600 md:hidden"
            >
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={cn(
          'overflow-hidden transition-all duration-300 md:hidden',
          mobileMenuOpen ? 'max-h-96 pb-4' : 'max-h-0'
        )}>
          <div className="flex flex-col gap-2 pt-4">
            <Link to="/tee-times" className="rounded-lg px-4 py-3 text-sm font-medium text-sand-700 hover:bg-sand-50">
              Tee Times
            </Link>
            <Link to="/shop" className="rounded-lg px-4 py-3 text-sm font-medium text-sand-700 hover:bg-sand-50">
              Pro Shop
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/bookings" className="rounded-lg px-4 py-3 text-sm font-medium text-sand-700 hover:bg-sand-50">
                  My Bookings
                </Link>
                <Link to="/loyalty" className="rounded-lg px-4 py-3 text-sm font-medium text-sand-700 hover:bg-sand-50">
                  Rewards
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}
